import { CommonModule, isPlatformBrowser } from "@angular/common";
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, inject, OnInit, PLATFORM_ID, ViewChild } from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MatCardModule } from "@angular/material/card";
import { MatDialogModule } from "@angular/material/dialog";
import { MatIconModule } from "@angular/material/icon";
import { MatPaginator, MatPaginatorModule } from "@angular/material/paginator";
import { MatSort, MatSortModule } from "@angular/material/sort";
import { MatTableDataSource, MatTableModule } from "@angular/material/table";
import { MatTooltipModule } from "@angular/material/tooltip";
import { LoaderComponent } from "../../../general/components/loader/loader.component";
import { GlassPillDirective } from "../../../general/directives/glass-pill.directive";
import { ReferralUrlService, ReferralUrlWithCount } from "../../../../features/sales/services/referral-url.service";
import { Chart, registerables } from 'chart.js';
Chart.register(...registerables);

@Component({
  selector: 'app-referral-urls-admin',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatIconModule,
    MatButtonModule,
    MatPaginatorModule,
    MatTooltipModule,
    MatDialogModule,
    MatSortModule,
    MatCardModule,
    LoaderComponent,
    GlassPillDirective
  ],
  templateUrl: './referral-urls.html',
  styleUrl: './referral-urls.scss',
})
export class ReferralUrls implements OnInit {
    referralUrlService = inject(ReferralUrlService);
    cdr = inject(ChangeDetectorRef);
    dataSource = new MatTableDataSource<ReferralUrlWithCount>();
    isLoading = false;
    displayedColumns: string[] = ['agent', 'url', 'count', 'created', 'updated'];
    platformId = inject(PLATFORM_ID);
    @ViewChild('clickChart') clickChart!: ElementRef<HTMLCanvasElement>;
    chart: Chart | undefined;
    @ViewChild(MatPaginator) set matPaginator(mp: MatPaginator) {
        this.dataSource.paginator = mp;
    }

    @ViewChild(MatSort) set matSort(ms: MatSort) {
        this.dataSource.sort = ms;
    }

    ngOnInit() {
        this.loadReferrals();
    }

    loadReferrals() {
        this.referralUrlService.getAllReferrals().subscribe(result => {
            this.dataSource.data = result;
            this.updateChart(result);
            this.cdr.detectChanges();
        });
    }

    updateChart(data: ReferralUrlWithCount[]) {
        if (!isPlatformBrowser(this.platformId)) {
            return;
        }

        setTimeout(() => {
            if (!this.clickChart) return;

            const topXLimit = 10; 
            const topUrls = [...data]
                .sort((a, b) => (b.count ?? 0) - (a.count ?? 0))
                .slice(0, topXLimit);

            // 1. Map to the agent name instead of the URL
            // Optional: .split(' (')[0] cleans up "Joe Wheatley (1001)" to just "Joe Wheatley"
            const labels = topUrls.map(item => {
                const agentName = item.agentName || 'Unknown Agent';
                return agentName.includes(' (') ? agentName.split(' (')[0] : agentName;
            });

            const counts: number[] = topUrls.map(item => item.count ?? 0); 

            if (this.chart) {
                this.chart.destroy();
            }

            this.chart = new Chart(this.clickChart.nativeElement, {
                type: 'bar',
                data: {
                    labels: labels, // Applied here
                    datasets: [{
                        label: 'Clicks',
                        data: counts,
                        backgroundColor: '#0d8e8c', 
                        borderColor: '#0d508e',
                        borderWidth: 1,
                        borderRadius: 22
                    }]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: {
                        y: {
                            beginAtZero: true,
                            grid: { color: 'rgba(0, 0, 0, 0.2)' },
                            ticks: { precision: 0, color: 'rgba(0, 0, 0, 0.87)' }
                        },
                        x: {
                            grid: { display: false },
                            ticks: { 
                                color: 'rgba(0, 0, 0, 0.87)',
                                maxRotation: 45, // Rotates labels if they start crowded together
                                minRotation: 0
                            }
                        }
                    },
                    plugins: {
                        legend: { display: false },
                        // 2. Optional: Add a tooltip configuration so hover shows the full details
                        tooltip: {
                            callbacks: {
                                title: (context) => {
                                    // Shows the full original agent string on hover
                                    const index = context[0].dataIndex;
                                    return topUrls[index].agentName || '';
                                },
                                afterTitle: (context) => {
                                    // Shows the associated URL beneath the name in the tooltip
                                    const index = context[0].dataIndex;
                                    return `URL: ${topUrls[index].referralUrl || ''}`;
                                }
                            }
                        }
                    }
                }
            });
        }, 0);
    }
    private getShortUrl(url: string): string {
        try {
            const parsed = new URL(url);
            return parsed.pathname !== '/' ? parsed.hostname + parsed.pathname : parsed.hostname;
        } catch {
            return url.length > 25 ? url.substring(0, 25) + '...' : url;
        }
    }
}